import { useCallback, useEffect, useRef, useState } from "react";

type UseScoutAllowMutationsInput = {
  conversationId: string | undefined;
  conversationAllowMutations: boolean | undefined;
  /**
   * When true, reset the toggle whenever `conversationId` becomes undefined
   * (e.g. popover session reset). When false, preserve local state across the
   * undefined→id transition after the user's first message on the main page.
   */
  resetWhenConversationClears?: boolean;
};

export function useScoutAllowMutations(input: UseScoutAllowMutationsInput) {
  const { conversationId, conversationAllowMutations, resetWhenConversationClears = false } = input;
  const [allowMutations, setAllowMutations] = useState<boolean>(false);
  const userTouchedRef = useRef(false);
  const lastConversationIdRef = useRef<string | undefined>(conversationId);

  useEffect(() => {
    const previousConversationId = lastConversationIdRef.current;
    lastConversationIdRef.current = conversationId;

    if (resetWhenConversationClears && !conversationId) {
      userTouchedRef.current = false;
      setAllowMutations(false);
      return;
    }

    if (!previousConversationId) {
      return;
    }

    if (previousConversationId === conversationId) {
      return;
    }

    userTouchedRef.current = false;
    setAllowMutations(conversationAllowMutations ?? false);
  }, [conversationAllowMutations, conversationId, resetWhenConversationClears]);

  useEffect(() => {
    if (!userTouchedRef.current) {
      setAllowMutations(conversationAllowMutations ?? false);
    }
  }, [conversationAllowMutations]);

  const onAllowMutationsChange = useCallback((next: boolean) => {
    userTouchedRef.current = true;
    setAllowMutations(next);
  }, []);

  return { allowMutations, onAllowMutationsChange };
}
